import React, { useRef, useState } from 'react'

import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
} from '@mui/material'
import Dropzone, { type DropzoneRef, type FileRejection } from 'react-dropzone'

import { getDate, numberFormatter } from '@albacross/frontend-utils'

import { useMatchResults, useMatchUpload } from 'hooks/useMatch'
import { downloadCsv } from 'utils/downloadCsv'

import './CompaniesUploadCSVModal.scss'

const errorMessages: Record<string, string> = {
  fileSize: 'file size',
  format: 'file format',
  noMatch: 'no matched companies',
  quantity: 'too many items',
  size: 'dimensions',
}

const VALID_EXTENSIONS = ['text/csv', 'application/vnd.ms-excel']

// v19 `accept` is a MIME -> extensions map; the extensions are static so build once.
const dropzoneAccept = Object.fromEntries(VALID_EXTENSIONS.map(mime => [mime, [] as string[]]))

const mapMatchedCompany = (company: any) => ({
  name: company.name,
  country: company.country,
  website: company.url,
  city: company.address?.city,
  number: company.number,
  employees: company.employees ? `${company.employees.from}-${company.employees.to}` : '',
  financial_report: company.financial_report
    ? // eslint-disable-next-line max-len
      `${numberFormatter(company.financial_report.revenue_from)}-${numberFormatter(company.financial_report.revenue_to)}`
    : '',
  nace_code: company.nace_code?.code,
  uuid: company.uuid,
})

type CompaniesUploadCSVModalProps = {
  open: boolean
  onClose: (...args: any[]) => any
}

const INITIAL_STATE = {
  matched: null as any,
  error: false as any,
  loading: false,
  validFileSize: true as any,
  validFormat: true as any,
  validQuantity: true as any,
  file: null as any,
  ipRequired: true,
}

const CompaniesUploadCSVModal = ({ open, onClose }: CompaniesUploadCSVModalProps) => {
  const { data: match } = useMatchResults()
  const upload = useMatchUpload()
  const dropzone = useRef<DropzoneRef | null>(null)
  const [state, setState] = useState(INITIAL_STATE)
  const patch = (p: Partial<typeof INITIAL_STATE>) => setState(s => ({ ...s, ...p }))

  const handleDrop = async (f: File[], rejectedFile: FileRejection[]) => {
    patch({ loading: true })
    // react-dropzone v6+ delivers rejections as { file, errors }; the component validates format itself, so flatten back to the raw File list.
    const files = [...f, ...rejectedFile.map(rejection => rejection.file)]

    const validQuantity = files.length === 1
    const validFileSize = files[0].size < 2000000
    const validFormat = VALID_EXTENSIONS.includes(files[0].type)
    const hasErrors = !(validQuantity && validFileSize && validFormat)

    let matched = null
    try {
      const body: any = await upload.mutateAsync({ file: files[0], ipRequired: state.ipRequired })
      matched = body && body.matched.map(mapMatchedCompany)
    } catch {
      // upload errors surface via match.errors below
    }

    patch({
      matched,
      error: hasErrors,
      loading: false,
      validFileSize,
      validFormat,
      validQuantity,
      file: !hasErrors && files[0],
    })
  }

  const handleIpRequiredChange = () => patch({ ipRequired: !state.ipRequired })

  const renderPlaceholder = () => [
    <CloudUploadIcon color="action" key={1} sx={{ fontSize: 40 }} />,
    <div className="companies-upload-csv-modal__text" key={2}>
      {'Your CSV file needs at least two mandatory columns: “country” and either “company_name”, ' +
        '“company_number” or “domain”.'}
    </div>,
    <Button className="companies-upload-csv-modal__button" key={3} onClick={() => dropzone.current?.open()}>
      Choose file
    </Button>,
  ]

  const renderUploadedFile = () => {
    const { file, matched } = state

    const handleDownload = () => {
      downloadCsv(matched, 'target-list-' + getDate(new Date()) + '.csv')
      patch({ matched: null, error: null, validFileSize: null, validFormat: null, validQuantity: null })
    }

    return [
      <CheckCircleIcon color="success" key={1} sx={{ fontSize: 40 }} />,
      <div className="companies-upload-csv-modal__text" key={2}>{`You successfully uploaded: ${file.name}`}</div>,
      <div className="companies-upload-csv-modal__text" key={3}>
        {`We matched ${match.matched.size} of ${match.total} entries`}
      </div>,
      <a
        className="companies-upload-csv-modal__download"
        key={4}
        role="button"
        tabIndex={0}
        style={{ cursor: 'pointer' }}
        onClick={handleDownload}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') handleDownload()
        }}>
        Click here to download
      </a>,
    ]
  }

  const renderErrorMessage = (type: string, isValid: any) => !isValid && <span>{errorMessages[type]}</span>

  const renderValidationMessage = () => (
    <div>
      <span className="companies-upload-csv-modal__dropzone__invalid-label">Invalid</span>
      {renderErrorMessage('quantity', state.validQuantity)}
      {renderErrorMessage('fileSize', state.validFileSize)}
      {renderErrorMessage('format', state.validFormat)}
    </div>
  )

  const renderValidationMessages = () => {
    const validationErrors = match.errors.map((error: any) => (
      <div className="companies-upload-csv-modal__dropzone__invalid-label" key={error}>
        {error}
      </div>
    ))
    const formErrors = state.error ? <div>{renderValidationMessage()}</div> : null

    return [...validationErrors, formErrors]
  }

  const { error, loading, matched, ipRequired } = state

  return (
    <Dialog onClose={onClose} open={open}>
      <DialogTitle>Upload CSV File</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} style={{ width: 550 }}>
          <Grid size={{ sm: 12 }}>
            <FormControlLabel
              control={<Checkbox checked={ipRequired} name="ip_required" onChange={handleIpRequiredChange} />}
              label="Match only companies with known IP"
            />
            <Dropzone
              accept={dropzoneAccept}
              maxSize={2000000}
              multiple={false}
              noClick
              onDrop={handleDrop}
              ref={dropzone}>
              {({ getRootProps, getInputProps }) => (
                <div {...getRootProps({ className: 'companies-upload-csv-modal__dropzone' })}>
                  <input {...getInputProps()} />
                  {loading ? (
                    'uploading file...'
                  ) : (
                    <div className="companies-upload-csv-modal__dropzone-text">
                      {!matched && renderPlaceholder()}
                      {(error || match.errors) && renderValidationMessages()}
                      {matched && renderUploadedFile()}
                    </div>
                  )}
                </div>
              )}
            </Dropzone>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CompaniesUploadCSVModal
